import { useState, useMemo, useEffect } from 'react'
import { getPosFlags, savePosFlag, resolvePosFlag, getAllPosProducts, getRecipes, getRecipeByProductId, calculateRecipeCost, getInventoryItems, getPosSales, getProductCostCard } from '../../lib/posStore'
import { useDepartment } from '../../context/DepartmentContext'
import { getFlagThresholds, saveFlagThresholds } from '../../data/posSeed'
import { AlertTriangle, Check, X, Shield, Search, Filter, Settings, Bell, TrendingDown, Package, DollarSign, FileText, ShoppingBag, RefreshCw } from 'lucide-react'

const SEVERITY_CONFIG = {
  CRITICAL: { bg: 'bg-red-950/30', border: 'border-red-800/50', badge: 'bg-red-900 text-red-300', icon: '🚨' },
  HIGH: { bg: 'bg-red-950/20', border: 'border-red-800/30', badge: 'bg-red-900/70 text-red-300', icon: '🔴' },
  MEDIUM: { bg: 'bg-amber-950/20', border: 'border-amber-800/30', badge: 'bg-amber-900 text-amber-300', icon: '🟠' },
  WARNING: { bg: 'bg-yellow-950/20', border: 'border-yellow-800/30', badge: 'bg-yellow-900 text-yellow-300', icon: '🟡' },
}

const FLAG_DESCRIPTIONS = {
  NEGATIVE_MARGIN: { label: 'Negative Margin', desc: 'Product selling below cost', action: 'Review recipe cost or adjust selling price' },
  HIGH_COGS_VARIANCE: { label: 'High COGS Variance', desc: 'Actual cost exceeds theoretical by >15%', action: 'Investigate wastage or recipe accuracy' },
  LOW_STOCK: { label: 'Low Stock', desc: 'Ingredient below reorder level', action: 'Place purchase order' },
  OUT_OF_STOCK: { label: 'Out of Stock', desc: 'Ingredient at zero', action: 'Urgent: restock immediately' },
  PRICE_INCREASE: { label: 'Price Increase', desc: 'Supplier price increased', action: 'Review supplier alternatives or adjust menu prices' },
  HIGH_WASTAGE: { label: 'High Wastage', desc: 'Wastage above threshold', action: 'Review production quantities and storage' },
  LOW_MARGIN: { label: 'Low Margin', desc: 'Gross margin below 20%', action: 'Review pricing or reduce ingredient costs' },
  MISSING_RECIPE: { label: 'Missing Recipe', desc: 'Product sold without recipe/costing', action: 'Create recipe in Product Management' },
  INVOICE_UNMATCHED: { label: 'Invoice Unmatched', desc: 'Purchase without invoice uploaded', action: 'Upload supplier invoice' },
  STOCKTAKE_VARIANCE: { label: 'Stocktake Variance', desc: 'Physical count differs >5%', action: 'Investigate discrepancy' },
  SALES_DROP: { label: 'Sales Drop', desc: 'Revenue down >20% from previous period', action: 'Review pricing, marketing, or product availability' },
  OVERHEAD_BREACH: { label: 'Overhead Breach', desc: 'Revenue below breakeven', action: 'Increase sales or reduce overhead costs' },
}

function formatR(v) { return `R${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` }

// Auto-scan for flags
function runFlagScan(departmentId) {
  const today = new Date().toISOString().split('T')[0]
  const products = getAllPosProducts(departmentId)
  const inventory = getInventoryItems(departmentId)
  const thresholds = getFlagThresholds()
  const newFlags = []

  // Check product margins
  for (const product of products) {
    if (!product.isActive) continue
    const costCard = getProductCostCard(product.id)
    if (!costCard) continue

    if (costCard.grossMargin < 0) {
      newFlags.push({
        flagCode: 'NEGATIVE_MARGIN', severity: 'CRITICAL', date: today,
        productId: product.id, productName: product.name, productCode: product.code,
        detail: `${product.name}: selling at ${formatR(product.sellingPrice)}, COGS is ${formatR(costCard.cogs)} — loss of ${formatR(-costCard.grossProfit)} per unit`,
      })
    } else if (costCard.grossMargin < thresholds.lowMarginPct) {
      newFlags.push({
        flagCode: 'LOW_MARGIN', severity: 'WARNING', date: today,
        productId: product.id, productName: product.name, productCode: product.code,
        detail: `${product.name}: gross margin is only ${costCard.grossMargin.toFixed(1)}% (threshold: ${thresholds.lowMarginPct}%)`,
      })
    }

    // Check for missing recipe on manufactured products
    if (product.type === 'manufactured') {
      const recipe = getRecipeByProductId(product.id)
      if (!recipe || !recipe.ingredients || recipe.ingredients.length === 0) {
        newFlags.push({
          flagCode: 'MISSING_RECIPE', severity: 'WARNING', date: today,
          productId: product.id, productName: product.name, productCode: product.code,
          detail: `${product.name} (${product.code}) is manufactured but has no recipe defined`,
        })
      }
    }
  }

  // Check inventory levels
  for (const item of inventory) {
    if (item.currentStock <= 0) {
      newFlags.push({
        flagCode: 'OUT_OF_STOCK', severity: 'HIGH', date: today,
        inventoryItemId: item.id, productName: item.name,
        detail: `${item.name} is out of stock (0 ${item.unit})`,
      })
    } else if (item.reorderLevel && item.currentStock <= item.reorderLevel) {
      newFlags.push({
        flagCode: 'LOW_STOCK', severity: 'HIGH', date: today,
        inventoryItemId: item.id, productName: item.name,
        detail: `${item.name}: ${item.currentStock} ${item.unit} remaining (reorder at ${item.reorderLevel})`,
      })
    }
  }

  // Save all new flags
  for (const flag of newFlags) {
    savePosFlag({ ...flag, departmentId })
  }

  return newFlags.length
}

export default function FlagsConsole() {
  const { currentDepartmentId } = useDepartment()
  const [tab, setTab] = useState('active')
  const [filter, setFilter] = useState('')
  const [severityFilter, setSeverityFilter] = useState('')
  const [resolveId, setResolveId] = useState(null)
  const [resolveNotes, setResolveNotes] = useState('')
  const [showThresholds, setShowThresholds] = useState(false)
  const [thresholds, setThresholds] = useState(getFlagThresholds())
  const [scanResult, setScanResult] = useState(null)

  const flags = useMemo(() => {
    let all = getPosFlags(currentDepartmentId, tab === 'active' ? false : tab === 'resolved' ? true : undefined)
    if (filter) {
      const q = filter.toLowerCase()
      all = all.filter(f => (f.productName || '').toLowerCase().includes(q) || (f.flagCode || '').toLowerCase().includes(q) || (f.detail || '').toLowerCase().includes(q))
    }
    if (severityFilter) all = all.filter(f => f.severity === severityFilter)
    return all
  }, [currentDepartmentId, tab, filter, severityFilter, scanResult])

  const counts = useMemo(() => {
    const all = getPosFlags(currentDepartmentId, false)
    return {
      total: all.length,
      critical: all.filter(f => f.severity === 'CRITICAL').length,
      high: all.filter(f => f.severity === 'HIGH').length,
      medium: all.filter(f => f.severity === 'MEDIUM').length,
      warning: all.filter(f => f.severity === 'WARNING').length,
    }
  }, [currentDepartmentId, scanResult])

  const handleResolve = (id) => {
    resolvePosFlag(id, resolveNotes)
    setResolveId(null)
    setResolveNotes('')
    setScanResult(Date.now()) // refresh
  }

  const handleScan = () => {
    const count = runFlagScan(currentDepartmentId)
    setScanResult(Date.now())
  }

  const handleSaveThresholds = () => {
    saveFlagThresholds(thresholds)
    setShowThresholds(false)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Shield size={20} className="text-brand-400" /> Flags & Alerts Console
        </h1>
        <div className="flex items-center gap-2">
          <button onClick={handleScan} className="flex items-center gap-1.5 bg-brand-700 hover:bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
            <RefreshCw size={14} /> Run Scan
          </button>
          <button onClick={() => setShowThresholds(!showThresholds)} className="flex items-center gap-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-2 rounded-lg text-xs">
            <Settings size={14} /> Thresholds
          </button>
        </div>
      </div>

      {/* Severity summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-3 cursor-pointer hover:border-gray-600" onClick={() => setSeverityFilter('')}>
          <p className="text-xs text-gray-400">Total Active</p>
          <p className="text-2xl font-bold text-white">{counts.total}</p>
        </div>
        <div className="bg-red-950/30 border border-red-800/30 rounded-lg p-3 cursor-pointer hover:border-red-600" onClick={() => setSeverityFilter('CRITICAL')}>
          <p className="text-xs text-red-400">🚨 Critical</p>
          <p className="text-2xl font-bold text-red-400">{counts.critical}</p>
        </div>
        <div className="bg-red-950/20 border border-red-800/20 rounded-lg p-3 cursor-pointer hover:border-red-600" onClick={() => setSeverityFilter('HIGH')}>
          <p className="text-xs text-red-300">🔴 High</p>
          <p className="text-2xl font-bold text-red-300">{counts.high}</p>
        </div>
        <div className="bg-amber-950/20 border border-amber-800/20 rounded-lg p-3 cursor-pointer hover:border-amber-600" onClick={() => setSeverityFilter('MEDIUM')}>
          <p className="text-xs text-amber-400">🟠 Medium</p>
          <p className="text-2xl font-bold text-amber-400">{counts.medium}</p>
        </div>
        <div className="bg-yellow-950/20 border border-yellow-800/20 rounded-lg p-3 cursor-pointer hover:border-yellow-600" onClick={() => setSeverityFilter('WARNING')}>
          <p className="text-xs text-yellow-400">🟡 Warning</p>
          <p className="text-2xl font-bold text-yellow-400">{counts.warning}</p>
        </div>
      </div>

      {/* Threshold settings */}
      {showThresholds && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-medium">Flag Thresholds</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-gray-400">Low Margin (%)</label>
              <input type="number" value={thresholds.lowMarginPct} onChange={e => setThresholds(t => ({ ...t, lowMarginPct: Number(e.target.value) }))} className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white" />
            </div>
            <div>
              <label className="text-xs text-gray-400">COGS Variance (%)</label>
              <input type="number" value={thresholds.highCogsVariancePct} onChange={e => setThresholds(t => ({ ...t, highCogsVariancePct: Number(e.target.value) }))} className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white" />
            </div>
            <div>
              <label className="text-xs text-gray-400">Stocktake Tolerance (%)</label>
              <input type="number" value={thresholds.stocktakeTolerancePct} onChange={e => setThresholds(t => ({ ...t, stocktakeTolerancePct: Number(e.target.value) }))} className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white" />
            </div>
            <div>
              <label className="text-xs text-gray-400">Sales Drop (%)</label>
              <input type="number" value={thresholds.salesDropPct} onChange={e => setThresholds(t => ({ ...t, salesDropPct: Number(e.target.value) }))} className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white" />
            </div>
          </div>
          <button onClick={handleSaveThresholds} className="bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 rounded text-sm">Save Thresholds</button>
        </div>
      )}

      {/* Tabs + filter */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1">
          {[{ id: 'active', label: 'Active' }, { id: 'resolved', label: 'Resolved' }, { id: 'all', label: 'All' }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === t.id ? 'bg-brand-700 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input value={filter} onChange={e => setFilter(e.target.value)} className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white" placeholder="Search flags..." />
        </div>
        {severityFilter && (
          <button onClick={() => setSeverityFilter('')} className="flex items-center gap-1 bg-gray-800 text-gray-400 px-3 py-2 rounded text-xs">
            Clear filter <X size={12} />
          </button>
        )}
      </div>

      {/* Flags list */}
      <div className="space-y-2">
        {flags.map(flag => {
          const sev = SEVERITY_CONFIG[flag.severity] || SEVERITY_CONFIG.WARNING
          const info = FLAG_DESCRIPTIONS[flag.flagCode] || {}
          return (
            <div key={flag.id} className={`${sev.bg} border ${sev.border} rounded-lg p-4`}>
              <div className="flex items-start gap-3">
                <div className="shrink-0 mt-0.5">
                  <span className={`text-xs px-2 py-0.5 rounded ${sev.badge}`}>{flag.severity}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono text-gray-400">{flag.flagCode}</span>
                    {info.label && <span className="text-xs text-gray-500">— {info.label}</span>}
                    <span className="text-xs text-gray-600 ml-auto">{flag.date}</span>
                  </div>
                  <p className="text-sm text-white">{flag.detail}</p>
                  {flag.productCode && <p className="text-xs text-gray-500 mt-1">Product: {flag.productName} ({flag.productCode})</p>}
                  {info.action && <p className="text-xs text-brand-400 mt-1">→ {info.action}</p>}
                  {flag.resolved && (
                    <div className="mt-2 bg-green-950/30 rounded px-3 py-1.5 text-xs text-green-400">
                      Resolved {flag.resolvedAt?.split('T')[0]} {flag.resolvedNotes && `— ${flag.resolvedNotes}`}
                    </div>
                  )}
                </div>
                <div className="shrink-0">
                  {!flag.resolved && (
                    resolveId === flag.id ? (
                      <div className="flex items-center gap-2">
                        <input value={resolveNotes} onChange={e => setResolveNotes(e.target.value)} className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white w-32" placeholder="Notes..." />
                        <button onClick={() => handleResolve(flag.id)} className="text-green-400 hover:text-green-300"><Check size={16} /></button>
                        <button onClick={() => setResolveId(null)} className="text-gray-500 hover:text-white"><X size={16} /></button>
                      </div>
                    ) : (
                      <button onClick={() => setResolveId(flag.id)} className="text-xs bg-gray-800 hover:bg-gray-700 text-gray-400 px-3 py-1.5 rounded">
                        Resolve
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          )
        })}
        {flags.length === 0 && (
          <div className="text-center py-12 text-gray-600">
            <Shield size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">{tab === 'active' ? 'No active flags — all clear!' : 'No flags found'}</p>
          </div>
        )}
      </div>
    </div>
  )
}
